
export type DayCell = {
  date: Date;
  dateStr: string; // YYYY-MM-DD
  day: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isWeekend: boolean;
  isHoliday: boolean;
  holidayName?: string;
};

// Taiwan Holidays (Consolidated)
export const TAIWAN_HOLIDAYS: Record<string, string> = {
  "2025-01-01": "元旦",
  "2025-01-25": "農曆春節",
  "2025-01-26": "農曆春節",
  "2025-01-27": "農曆春節",
  "2025-01-28": "農曆春節",
  "2025-01-29": "農曆春節",
  "2025-01-30": "農曆春節",
  "2025-01-31": "農曆春節",
  "2025-02-28": "和平紀念日",
  "2025-04-04": "兒童節",
  "2025-04-05": "清明節",
  "2025-05-31": "端午節",
  "2025-10-06": "中秋節",
  "2025-10-10": "國慶日",
  "2026-01-01": "元旦",
  "2026-02-17": "農曆除夕",
  "2026-02-18": "農曆春節",
  "2026-02-19": "農曆春節",
  "2026-02-20": "農曆春節",
  "2026-02-28": "和平紀念日",
  "2026-04-04": "兒童節/清明節",
  "2026-06-19": "端午節",
  "2026-09-25": "中秋節",
  "2026-10-10": "國慶日",
};

/**
 * Robustly convert any value to "YYYY-MM-DD" string.
 * Handles: string, Date, Firestore Timestamp, number.
 * Returns null if invalid or empty.
 */
export function toDateString(value: any): string | null {
  if (value === null || value === undefined) return null;

  // 1. Firestore Timestamp (Duck typing to avoid dependency)
  if (typeof value.toDate === 'function') {
    try {
      const d = value.toDate();
      if (d instanceof Date && !isNaN(d.getTime())) {
        return d.toISOString().slice(0, 10);
      }
    } catch { return null; }
  }

  // 2. Date Object
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value.toISOString().slice(0, 10);
  }

  // 3. String
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    
    // If ISO-like '2025-12-12T...'
    if (trimmed.includes("T")) {
      return trimmed.split("T")[0];
    }
    // If standard YYYY-MM-DD
    if (trimmed.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return trimmed;
    }
    // Attempt parse
    const d = new Date(trimmed);
    if (!isNaN(d.getTime())) {
      return d.toISOString().slice(0, 10);
    }
    // Fallback: return as is if length matches but risking format mismatch
    if (trimmed.length >= 10) return trimmed.slice(0, 10);
    return null;
  }

  // 4. Number (Timestamp)
  if (typeof value === 'number') {
    try {
      const d = new Date(value);
      if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    } catch { return null; }
  }

  return null;
}

/**
 * Compare two date-like values for sorting.
 * Handles null/undefined (treated as empty string).
 */
export function compareDateLike(a: any, b: any): number {
  const sa = toDateString(a) ?? "";
  const sb = toDateString(b) ?? "";
  return sa.localeCompare(sb);
}

/**
 * Universal date normalizer (Legacy Alias for compatibility)
 * Converts Date, Firestore Timestamp, ISO string, or number to "YYYY-MM-DD".
 * Returns empty string if invalid.
 */
export function toYMD(val: any): string {
  return toDateString(val) || "";
}

/**
 * Normalize any input to a JavaScript Date object if possible.
 * Returns null if invalid or empty.
 */
export function normalizeDateValue(value: any): Date | null {
  if (!value) return null;

  // Firestore Timestamp
  if (typeof value.toDate === 'function') {
    try {
      const d = value.toDate();
      return d instanceof Date && !isNaN(d.getTime()) ? d : null;
    } catch { return null; }
  }

  // Date Object
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  // String or Number
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

  return null;
}

/**
 * Format any date input to YYYY-MM-DD safely.
 * Alias for toYMD but ensures type safety logic internally.
 */
export function formatDateYmd(value: any): string {
  return toYMD(value);
}

/**
 * Format date to "MM/DD" (e.g., 12/25).
 * Safe for any input type. Returns "-" if invalid.
 */
export function formatDateMD(value: any): string {
  const s = toDateString(value);
  if (!s) return "-";
  // s is YYYY-MM-DD
  const parts = s.split("-");
  if (parts.length === 3) {
    return `${parts[1]}/${parts[2]}`;
  }
  return "-";
}

export function formatDate(date: Date | null, pattern: string = "YYYY-MM-DD"): string {
  if (!date || isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return pattern === "YYYY-MM-DD" ? `${y}-${m}-${d}` : `${y}/${m}/${d}`;
}

export function parseDate(input: string | null): Date | null {
  if (!input) return null;
  const d = new Date(input);
  return isNaN(d.getTime()) ? null : d;
}

export function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function getHolidayName(dateStr: string): string | null {
  return TAIWAN_HOLIDAYS[dateStr] || null;
}

export function isWeekend(dateStr: string): boolean {
  if (!dateStr) return false;
  const day = new Date(dateStr).getDay();
  return day === 0 || day === 6; // 0=Sun, 6=Sat
}

export function addBusinessDays(startDateStr: string, days: number): string {
  let count = 0;
  let current = new Date(startDateStr);
  while (count < days) {
    current.setDate(current.getDate() + 1);
    const iso = formatDate(current);
    // Skip Weekend (0=Sun, 6=Sat)
    const day = current.getDay();
    if (day !== 0 && day !== 6) {
      count++;
    }
  }
  return formatDate(current);
}

/**
 * Returns a 6x7 matrix of DayCells for the given month
 */
export function getMonthMatrix(year: number, month: number): DayCell[][] {
  // month is 0-indexed (0=Jan, 11=Dec)
  const firstDayOfMonth = new Date(year, month, 1);
  const startDay = firstDayOfMonth.getDay(); // 0=Sun, 1=Mon...
  
  // Calculate start date of the grid (go back to Sunday)
  const startDate = new Date(firstDayOfMonth);
  startDate.setDate(startDate.getDate() - startDay);

  const matrix: DayCell[][] = [];
  const current = new Date(startDate);
  const todayStr = formatDate(new Date());

  for (let row = 0; row < 6; row++) {
    const week: DayCell[] = [];
    for (let col = 0; col < 7; col++) {
      const dateStr = formatDate(current);
      const isWeekend = current.getDay() === 0 || current.getDay() === 6;
      const holidayName = getHolidayName(dateStr) || undefined;

      week.push({
        date: new Date(current),
        dateStr,
        day: current.getDate(),
        isCurrentMonth: current.getMonth() === month,
        isToday: dateStr === todayStr,
        isWeekend,
        isHoliday: !!holidayName,
        holidayName
      });
      current.setDate(current.getDate() + 1);
    }
    matrix.push(week);
  }

  return matrix;
}

export function getTodayKeyAsiaTaipei(date?: Date): string {
  // 用 Asia/Taipei 時區計算今天的年月日
  const now = date ?? new Date();

  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Taipei",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  // en-CA 預設格式就是 YYYY-MM-DD
  const formatted = formatter.format(now); // 例如 "2025-12-05"
  return formatted;
}

export function formatTitleDateAsiaTaipei(): string {
  return getTodayKeyAsiaTaipei();
}

/**
 * Formats a date that could be:
 * 1. Firestore Timestamp (has .toDate())
 * 2. Excel Serial Number (e.g., 46326.3333) -> Converts to YYYY-MM-DD
 * 3. Standard String (YYYY-MM-DD)
 */
export function formatExcelOrTimestamp(value: any): string {
  const s = toDateString(value);
  if (s) return s;

  // Excel logic fallback
  const asNumber = Number(value);
  if (!isNaN(asNumber) && isFinite(asNumber) && asNumber > 30000 && asNumber < 60000) {
      const excelBaseDate = new Date(Date.UTC(1899, 11, 30));
      const millis = excelBaseDate.getTime() + asNumber * 86400000;
      const d = new Date(millis);
      const roundedDate = new Date(Math.round(d.getTime() / 1000) * 1000);
      return formatDate(roundedDate);
  }

  return String(value ?? "-");
}
